import {createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../use-is-mounted";
import Api from "../../../core/services/api/api";
import {useAuthenticationState} from "../use-auth-state";


const PurchasingKeyContext = createContext({
    loadingPurchasingKeyForPlanning: true,
    purchasingKeyForPlanning: null,
    getPurchasingKeyForPlanning: async () => null,
});

export const PurchasingKeyProvider = ({children}) => {
    const {loggedIn} = useAuthenticationState();
    const [loadingPurchasingKeyForPlanning, setLoadingPurchasingKeyForPlanning] = useState(true);
    const [purchasingKey, setPurchasingKey] = useState({purchasingKeyForPlanning: null});
    const isMounted = useIsMounted();
    const keyRef = useRef(null);
    const inFlightRef = useRef(null);

    /**
     * Ensures the purchasing key for planning is loaded.
     * - Reuses a cached key when present (unless `force`).
     * - Dedupes concurrent callers onto one in-flight request.
     * - Does not flip loading when serving cache / attaching to an in-flight fetch.
     * @param {{force?: boolean}} [options]
     * @returns {Promise<string|null|undefined>}
     */
    const getPurchasingKeyForPlanning = useCallback(async ({force = false} = {}) => {
        if (!loggedIn) {
            return null;
        }
        if (!force && keyRef.current) {
            return keyRef.current;
        }
        if (!force && inFlightRef.current) {
            return inFlightRef.current;
        }

        setLoadingPurchasingKeyForPlanning(true);
        const request = (async () => {
            try {
                const response = await Api.getPurchasingKeyForPlanning();
                const key = response?.data?.key ?? null;
                if (!isMounted()) {
                    return key;
                }
                keyRef.current = key;
                setPurchasingKey(prevState => ({
                    ...prevState,
                    purchasingKeyForPlanning: key,
                }));
                setLoadingPurchasingKeyForPlanning(false);
                return key;
            } catch (error) {
                if (isMounted()) {
                    setLoadingPurchasingKeyForPlanning(false);
                }
                throw error;
            } finally {
                inFlightRef.current = null;
            }
        })();

        inFlightRef.current = request;
        return request;
    }, [isMounted, loggedIn]);

    /**
     * Load the key once when the user is authenticated (shell-level ensure).
     */
    useLayoutEffect(() => {
        if (!loggedIn) {
            keyRef.current = null;
            inFlightRef.current = null;
            setPurchasingKey({purchasingKeyForPlanning: null});
            setLoadingPurchasingKeyForPlanning(false);
            return;
        }
        void getPurchasingKeyForPlanning();
    }, [loggedIn, getPurchasingKeyForPlanning]);

    const providerValue = useMemo(() => ({
        loadingPurchasingKeyForPlanning,
        purchasingKeyForPlanning: purchasingKey.purchasingKeyForPlanning,
        getPurchasingKeyForPlanning,
    }), [loadingPurchasingKeyForPlanning, purchasingKey, getPurchasingKeyForPlanning]);

    return (
        <PurchasingKeyContext.Provider value={providerValue}>
            {children}
        </PurchasingKeyContext.Provider>
    );
}

/**
 * Returns the controller for the cached purchasing key.
 * When `load` is true, ensures the key is loaded (cache / in-flight safe).
 */
export const usePurchasingKeyController = ({load = false} = {}) => {
    const controller = useContext(PurchasingKeyContext);
    const {getPurchasingKeyForPlanning} = controller;

    /**
     * Ensure the key is available after mount when requested.
     */
    useLayoutEffect(() => {
        if (load) {
            void getPurchasingKeyForPlanning();
        }
    }, [load, getPurchasingKeyForPlanning]);

    return controller;
}

export default usePurchasingKeyController;
